import styled from 'styled-components';

import { MiniBadge } from '@xing-com/badge';
import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import {
  getTimeDifferenceInDays,
  getTimeDifferenceInHours,
} from '@xing-com/crate-jobs-helpers';
import { spaceXS } from '@xing-com/tokens';
import { BodyCopyLineHeight, Meta } from '@xing-com/typography';

const Notification = styled(MiniBadge)`
  margin-right: ${spaceXS};
`;

const StyledMeta = styled(Meta)`
  position: relative;
  width: fit-content;
`;

type TranslationValues = {
  id: string;
  values?: Record<string, number>;
};

const getTranslationValues = (date: string): TranslationValues => {
  const timeDifferenceInDays = getTimeDifferenceInDays(date);
  const timeDifferenceInHours = getTimeDifferenceInHours(date);

  if (timeDifferenceInHours < 1) {
    return { id: 'JOBS_TIMESTAMP_JUST_NOW' };
  }

  if (timeDifferenceInDays < 1) {
    return {
      id: 'JOBS_TIMESTAMP_N_HOURS_AGO',
      values: { number: Math.floor(timeDifferenceInHours) },
    };
  }

  if (timeDifferenceInDays <= 2) {
    return { id: 'JOBS_TIMESTAMP_YESTERDAY' };
  }

  if (timeDifferenceInDays <= 30) {
    return {
      id: 'JOBS_TIMESTAMP_N_DAYS_AGO',
      values: { number: Math.floor(timeDifferenceInDays) },
    };
  }

  return { id: 'JOBS_TIMESTAMP_30_PLUS_DAYS_AGO' };
};

type Props = {
  date: string;
  isNew?: boolean;
  noMargin?: boolean;
  lineHeight?: BodyCopyLineHeight;
  className?: string;
};

export const OptimisticPublicationDate: React.FC<Props> = ({
  date,
  isNew = false,
  ...props
}) => {
  const { id, values = {} } = getTranslationValues(date);

  return (
    <StyledMeta size="small" {...props}>
      {isNew ? <Notification data-testid="mini-badge" /> : null}
      <AriaExtendedText
        item={{ id, values }}
        a11yItem={{ id: `${id}_A11Y`, values }}
        noWrapper
      />
    </StyledMeta>
  );
};
